import { Fragment, h } from 'preact'
import { useAppState, useStore } from '../../../../lib/redux-provider-nohooks'
import { useLocale, useLocalizedValue } from '../../../../lib/intl/IntlProvider'
import { message, Message } from '../../../../lib/intl/Message'
import { Geografi, isGeografi, isParti, isValgResultat, Parti } from '../../../domain'
import appStyle from '../../frames/AppFrame/AppFrame.css'
import { ResultsState } from '../../../store/state'
import { fetchFylkesresutater } from '../../../store/actions/fylkesresultat'
import { fetchKommuneresultat } from '../../../store/actions/kommuneresultat'
import { OSLO_KOMMUNE } from '../../shared/geografi'
import { Status } from '../shared/Status'
import { Icon } from '../../shared/Icons/Icons'
import { createUrlFor } from '../../../routes'
import { LoadingSpinner } from '../../shared/LoadingSpinner/LoadingSpinner'
import { useCustomContext } from '../../../../lib/context'
import { GA_EVENTS } from '../../../../lib/analytics'

import s from './SekundarHeading.module.css'

type Props = {
  currentPage: Geografi | Parti
  resultat: ResultsState
  type: 'kommune' | 'fylke'
  load: () => void
}

export function SekundarHeading(props: Props) {
  const { currentPage, type, resultat, load } = props
  const locale = useLocale()
  const localize = useLocalizedValue()

  const isLoading = resultat.state === 'reloading' || resultat.state === 'loading'
  const id = type === 'kommune' ? 'kommunevalg' : 'fylkestingsvalg'

  function localizedName() {
    if (currentPage.navn.nb.toLowerCase().indexOf('uoppgitt') !== -1) {
      return isGeografi(currentPage) && currentPage.type === 'Bydel'
        ? message('uoppgitt-bydel')
        : message('uoppgitt-krets')
    } else {
      return localize(currentPage.navn)
    }
  }

  return (
    <div class={s.sekundarHeading + ' ' + appStyle.section} id={id}>
      <div class={s.container}>
        <h2>
          {/* eslint-disable jsx-a11y/aria-role,jsx-a11y/click-events-have-key-events */}
          <div
            role="text"
            onClick={resultat.state === 'empty' ? load : undefined}
            style={{ cursor: resultat.state === 'empty' ? 'pointer' : '' }}
          >
            <div class={s.mainHeading}>
              {isGeografi(currentPage) && currentPage.type === 'Nasjon' ? (
                <Fragment>
                  <strong>
                    <Message id="tittel-landsoversikt" />
                  </strong>{' '}
                  {localize(currentPage.navn)}
                </Fragment>
              ) : (
                <strong>{localizedName()}</strong>
              )}
            </div>
            <Message id={'valgtype-' + type} />
          </div>
        </h2>
        {resultat.state === 'empty' ? (
          <button class={s.loadButton} onClick={load}>
            <Icon type="nrk-chevron-down" />
            <Message id="last-resultat" class="nrk-sr" />
          </button>
        ) : (
          <Fragment>
            {isGeografi(currentPage) && <Status resultat={resultat} />}
            {isParti(currentPage) && resultat.state === 'success' && isValgResultat(resultat.data) && (
              <div class={s.listeInfo}>
                <Message id="header-liste-info" />{' '}
                <a href={createUrlFor(resultat.data.geografi, locale)}>
                  {localize(resultat.data.geografi.navn)}
                </a>
              </div>
            )}
          </Fragment>
        )}
      </div>
      <div class={s.loadingSpinnerWrapper}>
        <LoadingSpinner isLoading={isLoading} />
      </div>
    </div>
  )
}

type ConnectedProps = {
  type: 'kommune' | 'fylke'
}

export function ConnectedSekundarHeading(props: ConnectedProps) {
  const { page, kommuneresultat, fylkesresultat } = useAppState()
  const { sendGaEvent } = useCustomContext()

  const store = useStore()
  const currentPage = page.current
  if (!isGeografi(currentPage) && !isParti(currentPage)) {
    return null
  }

  /* Sekundærvalg skal ikke vises i Oslo kommune */
  if (
    isGeografi(currentPage) &&
    (currentPage.key === OSLO_KOMMUNE ||
      (currentPage.parent && currentPage.parent.key === OSLO_KOMMUNE))
  ) {
    return null
  }

  const load = () => {
    sendGaEvent(GA_EVENTS.LOAD_SEKUNDAR(props.type))
    if (props.type === 'kommune') {
      return store.dispatch(fetchKommuneresultat(currentPage))
    } else {
      return store.dispatch(fetchFylkesresutater(currentPage))
    }
  }

  const resultat = props.type === 'kommune' ? kommuneresultat : fylkesresultat
  return (
    <SekundarHeading currentPage={currentPage} type={props.type} resultat={resultat} load={load} />
  )
}
